// 38, -96 is around the center of the continental US
export function findCenterCoordinate(coords: (number | undefined)[][]) {
	if (!coords || !coords.length) {
		return { lat: 38, lng: -96 };
	}

	const filtered = coords.filter((item) => item[0] !== undefined && item[1] !== undefined);

	const x = filtered.map((item) => item[0] as number);
	const y = filtered.map((item) => item[1] as number);

	const minX = Math.min(...x);
	const maxX = Math.max(...x);
	const minY = Math.min(...y);
	const maxY = Math.max(...y);

	if (minX === Infinity || maxX === -Infinity || minY === Infinity || maxY === -Infinity) {
		return { lat: 38, lng: -96 };
	}

	return {
		lat: (minX + maxX) / 2,
		lng: (minY + maxY) / 2,
		bounds: {
			north: maxY,
			south: minY,
			east: maxX,
			west: minX,
		},
	};
}

export function getDistanceInMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const R = 3959; // Earth's radius in miles
	const dLat = (lat2 - lat1) * (Math.PI / 180);
	const dLon = (lon2 - lon1) * (Math.PI / 180);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

// Usage example to filter an array of positions:
/**
 * @param centerLat
 * @param centerLon
 * @param positions (must have lat and lon properties)
 * @param radiusMiles
 */
export function filterPositionsWithinRadius<TPosition extends Position>(
	centerLat: number,
	centerLon: number,
	positions: TPosition[],
	radiusMiles: number = 50,
) {
	return positions.filter(
		(pos) => getDistanceInMiles(centerLat, centerLon, pos.lat, pos.lon ?? pos.lng!) <= radiusMiles,
	);
}

/**
 * Filters positions within a square bounding box. Faster but less accurate than radius filtering.
 * Uses approximately 69 miles per degree latitude and varies by latitude for longitude.
 * @param centerLat Center latitude
 * @param centerLon Center longitude
 * @param positions Array of positions with lat/lon properties
 * @param squareSideMiles Length of the square's side in miles (equivalent to diameter)
 */
export function filterPositionsWithinSquare<TPosition extends Position>(
	centerLat: number,
	centerLon: number,
	positions: TPosition[],
	squareSideMiles: number = 50,
) {
	// Convert miles to degrees
	const latDegrees = squareSideMiles / 69; // ~69 miles per degree of latitude
	const lonDegrees = squareSideMiles / (Math.cos(centerLat * (Math.PI / 180)) * 69);

	const latMin = centerLat - latDegrees;
	const latMax = centerLat + latDegrees;
	const lonMin = centerLon - lonDegrees;
	const lonMax = centerLon + lonDegrees;

	return positions.filter((pos) => {
		const lon = pos.lon ?? pos.lng!;
		return pos.lat >= latMin && pos.lat <= latMax && lon >= lonMin && lon <= lonMax;
	});
}

interface Position {
	lat: number;
	lon?: number;
	lng?: number;
}

/**
 * Deduplicates items by their lat/lon coordinates, keeping the one with the lowest ordinal.
 * When ordinals are equal, falls back to the latest datetime.
 * Null or undefined dates are treated as older than any valid date.
 */
export function dedupeByCoordinates<
	T extends { ordinal: number; lat: number; lon?: number; lng?: number; datetime?: Date | string | null },
>(items: T[]): T[] {
	const map = new Map<string, T>();
	for (const item of items) {
		// if no position, ignore the item
		if (!item.lat || !(item.lon ?? item.lng)) continue;
		if (!validateCoordinates(item.lat, item.lon ?? item.lng)) continue;

		const key = `${item.lat},${item.lon ?? item.lng}`;
		const existing = map.get(key);
		if (!existing) {
			map.set(key, item);
			continue;
		}

		if (item.ordinal < existing.ordinal) {
			map.set(key, item);
			continue;
		}

		if (item.ordinal > existing.ordinal) {
			continue;
		}

		// Same ordinal – fall back to datetime
		const existingTime = existing.datetime ? new Date(existing.datetime).getTime() : 0;
		const currentTime = item.datetime ? new Date(item.datetime).getTime() : 0;

		const eTime = isNaN(existingTime) ? 0 : existingTime;
		const cTime = isNaN(currentTime) ? 0 : currentTime;

		if (cTime >= eTime) {
			map.set(key, item);
		}
	}

	return Array.from(map.values());
}

export function validateCoordinates(lat: number, lon: number) {
	if (lat < -90) return false;
	if (lat > 90) return false;

	if (lon < -180) return false;
	if (lon > 180) return false;

	return true;
}
